import * as Sentry from "@sentry/react";
import { AppDispatch, RootState } from "@Reducers/index";
import axiosWrapper from "@AxiosWrapper";
import { setUserTagLoading, setUserTagData, setUpdatedUserProfileDetails } from "../reducers/userTagData";
import type { GetUserDetailsByIdRequest, UserTagProfileData } from "@ServerTypes/accounts/accounts.schema";
import type { Exact } from "@Helpers/types/exact";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { DEFAULT_SYSTEM_USER } from "@ServerTypes/helpers/systemUser.constants";

export const fetchUserTagData = (id: ObjectIdString) => async (dispatch: AppDispatch, getState: () => RootState) => {
  // "0" is the stored system-user sentinel (userId.schema.ts); it has no account to look up
  if (id === DEFAULT_SYSTEM_USER) return;
  const { userTagData } = getState();

  if (userTagData[id]?.isLoading || userTagData[id]?.isLoaded) {
    return;
  }

  dispatch(setUserTagLoading({ id }));

  try {
    // Fetch user details
    const response = await getUserDetailsById({ userId: id });
    // A refusal carries no row; it is stored like an account with no details, as the empty tag.
    const row = response.data.status ? response.data.result : undefined;
    // The row as the tag store keeps it: a field the account holds as null is stored absent, as for an
    // account without it, and the membership's `portal.organisation.name` is the store's `organisationName`.
    // The rest of the row (`status`, `portal.seat`, `customViews`) is not part of a tag.
    const userDetails = row && {
      _id: row._id,
      fullName: row.fullName ?? undefined,
      firstName: row.firstName ?? undefined,
      lastName: row.lastName ?? undefined,
      email: row.email ?? undefined,
      designation: row.designation ?? undefined,
      organisationName: row.portal?.organisation?.name ?? undefined,
      contactNo: row.contactNo ?? undefined,
      userLocation: row.userLocation ?? undefined,
      signature: row.signature,
      profilePic: row.profilePic,
    };

    // If there's a profile picture to fetch, get the image as a blob
    if (userDetails?.profilePic) {
      const imgResponse = await axiosWrapper.api.get("/fileSystemServer/getImage/:type/:id", {
        responseType: "blob",
        pathParams: { type: "profile", id: id },
      });

      // Use FileReader to convert blob to Base64
      const reader = new FileReader();

      reader.onloadend = function () {
        // `readAsDataURL` resolves to a data-URL string; anything else means the read failed, and
        // `UserTagProfileData.profilePic` is nullable, so that case stores no picture.
        const profilePic = typeof reader.result === "string" ? reader.result : null;

        // Dispatch the data with the Base64 image URL
        dispatch(setUserTagData({ id, data: { ...userDetails, profilePic } }));
      };

      // Read the Blob as Data URL
      reader.readAsDataURL(imgResponse.data);
    } else {
      // If there's no profile picture, just dispatch the data without it
      dispatch(setUserTagData({ id, data: { ...(userDetails ?? {}), profilePic: null } }));
    }
  } catch (error) {
    console.error("Error fetching user tag data:", error);
    // The rejected promise this used to build was only ever seen by the global
    // unhandled-rejection handler; reporting the bound error directly says the same thing.
    Sentry.captureException(error);
  }
};

export const updateUserProfileDetails = (data: UserTagProfileData) => (dispatch: AppDispatch) => {
  dispatch(setUpdatedUserProfileDetails(data));
};

export async function getUserDetailsById<T extends Exact<GetUserDetailsByIdRequest, T>>(data: T) {
  return await axiosWrapper.api.post("/accounts/getUserDetailsById", data);
}
